import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { showError } from "@/shared/utils/toast";
import { useUpdateAccountActionPlan } from "@/features/accounts/hooks/useAccounts";
import { useMainStore } from "@/stores";
import DeleteConfirmationModal from "@/shared/components/ui/Modal/DeleteConfirmationModal";
import {
  AccountPlanAddButton,
  AccountPlanDeleteButton,
  AccountPlanDueDateField,
  AccountPlanEditableActionInput,
  AccountPlanEditableDescriptionInput,
  AccountPlanNewActionInput,
  AccountPlanNewDescriptionInput,
  AccountPlanOwnerField,
  AccountPlanStatusField,
} from "./AccountPlanRowCells";

function CustomTableAccountPlan({ initialActions = [], users = [] }) {
  const { user } = useMainStore();
  const { mutate: updateAction } = useUpdateAccountActionPlan();
  const [rows, setRows] = useState(() => {
    return initialActions.length > 0 ? initialActions : [];
  });

  const [newRow, setNewRow] = useState({
    action: "",
    description: "",
    owner: "",
    dueDate: "",
    status: "",
  });

  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    if (initialActions.length > 0) {
      setRows(initialActions);
    }
  }, [initialActions]);

  const handleNewRowChange = (updates) => {
    setNewRow((prev) => ({ ...prev, ...updates }));
  };

  const commitNewRow = () => {
    const action = newRow.action.trim();
    const description = newRow.description.trim();
    const owner = newRow.owner.trim();
    const status = (newRow.status || "").trim();
    const dueDate = newRow.dueDate;

    if (!action) {
      showError("Enter an action before adding.");
      return;
    }
    if (!description) {
      showError("Enter a description before adding.");
      return;
    }
    if (!owner) {
      showError("Assign an owner before adding.");
      return;
    }
    if (!dueDate) {
      showError("Choose a due date before adding.");
      return;
    }
    if (!status) {
      showError("Select a status before adding.");
      return;
    }

    setRows([
      ...rows,
      {
        id: Date.now(),
        action,
        description,
        owner,
        dueDate,
        status,
      },
    ]);
    setNewRow({
      action: "",
      description: "",
      owner: "",
      dueDate: "",
      status: "",
    });
  };

  const handleDeleteRow = (id) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
    setDeleteTargetId(null);
  };

  const handleUpdateRow = (id, field, value) => {
    const row = rows.find((r) => r.id === id);
    if (!row || !user?.id || row[field] === value) return;

    setRows(
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );

    const updatePayload = {
      actionId: id,
      userId: user.id,
    };

    if (field === "owner") {
      updatePayload.owner = value || "";
    } else if (field === "dueDate") {
      updatePayload.dueDate = value || null;
    } else if (field === "status") {
      updatePayload.status = value || "";
    } else if (field === "action" || field === "description") {
      return;
    }

    updateAction(updatePayload);
  };

  const handleSelectUser = (data) => {
    const contactName = data?.contact?.name || data?.name || "";
    setNewRow((prev) => ({ ...prev, owner: contactName }));
  };

  const handleSelectUserForRow = (rowId, data) => {
    const contactName = data?.contact?.name || data?.name || "";
    handleUpdateRow(rowId, "owner", contactName);
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <Table
        aria-label="Account Action Plan table"
        id="account-plan-table"
        classNames={{
          base: "flex h-full min-h-0 flex-col gap-0",
          wrapper:
            "min-h-0 flex-1 overflow-y-auto shadow-none rounded-none p-0",
          table: "min-w-full border-separate border-spacing-0",
          thead: "[&_tr]:relative [&_tr]:z-20",
          th: "sticky top-0 z-20 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-100 font-normal text-[0.813rem] first:rounded-s-none first:rounded-tl-lg last:rounded-e-none last:rounded-tr-lg border-b border-gray-200 dark:border-gray-700",
          tbody:
            "[&_tr_td]:bg-white dark:[&_tr_td]:bg-gray-900 [&_tr:not(:last-child)_td]:border-b [&_tr:not(:last-child)_td]:border-gray-200 dark:[&_tr:not(:last-child)_td]:border-gray-800 [&_tr]:transition-colors hover:[&_tr_td]:bg-gray-50 dark:hover:[&_tr_td]:bg-gray-800/60",
          tr: "border-0",
          td: "py-4 align-top",
        }}
      >
        <TableHeader id="table-header-account-plan">
          <TableColumn className="text-[0.813rem] text-gray-800 dark:text-gray-100 font-normal">
            Actions
          </TableColumn>
          <TableColumn className="text-[0.813rem] text-gray-800 dark:text-gray-100 font-normal">
            Description
          </TableColumn>
          <TableColumn className="text-[0.813rem] text-gray-800 dark:text-gray-100 font-normal">
            Owner
          </TableColumn>
          <TableColumn className="text-[0.813rem] text-gray-800 dark:text-gray-100 font-normal">
            Due Date
          </TableColumn>
          <TableColumn className="text-[0.813rem] text-gray-800 dark:text-gray-100 font-normal">
            Status
          </TableColumn>
          <TableColumn className="text-[0.813rem] text-gray-800 font-normal w-16">
            {" "}
          </TableColumn>
        </TableHeader>
        <TableBody>
          <TableRow key="new">
            <TableCell>
              <AccountPlanNewActionInput
                value={newRow.action}
                onChange={(action) => handleNewRowChange({ action })}
              />
            </TableCell>
            <TableCell className="align-top">
              <AccountPlanNewDescriptionInput
                value={newRow.description}
                onChange={(description) => handleNewRowChange({ description })}
              />
            </TableCell>
            <TableCell>
              <AccountPlanOwnerField
                value={newRow.owner}
                users={users}
                onAssign={handleSelectUser}
              />
            </TableCell>
            <TableCell>
              <AccountPlanDueDateField
                value={newRow.dueDate}
                onChange={(dueDate) => handleNewRowChange({ dueDate })}
              />
            </TableCell>
            <TableCell>
              <AccountPlanStatusField
                value={newRow.status}
                onChange={(status) => handleNewRowChange({ status })}
              />
            </TableCell>
            <TableCell>
              <AccountPlanAddButton onCommit={commitNewRow} />
            </TableCell>
          </TableRow>
          {rows.map((row) => (
            <TableRow key={row.id} className="hover:bg-gray-50 w-full">
              <TableCell>
                <AccountPlanEditableActionInput
                  value={row.action}
                  onChange={(action) =>
                    handleUpdateRow(row.id, "action", action)
                  }
                />
              </TableCell>
              <TableCell className="align-top">
                <AccountPlanEditableDescriptionInput
                  value={row.description}
                  onChange={(description) =>
                    handleUpdateRow(row.id, "description", description)
                  }
                />
              </TableCell>
              <TableCell>
                <AccountPlanOwnerField
                  value={row.owner}
                  users={users}
                  onAssign={(data) => handleSelectUserForRow(row.id, data)}
                />
              </TableCell>
              <TableCell>
                <AccountPlanDueDateField
                  value={row.dueDate}
                  onChange={(dueDate) =>
                    handleUpdateRow(row.id, "dueDate", dueDate)
                  }
                />
              </TableCell>
              <TableCell>
                <AccountPlanStatusField
                  value={row.status}
                  onChange={(status) =>
                    handleUpdateRow(row.id, "status", status)
                  }
                />
              </TableCell>
              <TableCell>
                <AccountPlanDeleteButton
                  onDelete={() => setDeleteTargetId(row.id)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <DeleteConfirmationModal
        showConfirmDelete={deleteTargetId != null}
        onClose={() => setDeleteTargetId(null)}
        title="Remove this action?"
        description="This action will be removed from the plan. You can refresh the page to restore server-saved items."
        onDelete={() =>
          deleteTargetId != null && handleDeleteRow(deleteTargetId)
        }
        deleteButtonText="Remove"
        cancelButtonText="Cancel"
        width="400px"
        isDeleting={false}
      />
    </div>
  );
}

export default CustomTableAccountPlan;
